import { NON_ESSENTIAL_CATEGORIES } from "./categories";
import type { CategoryConsent, ConsentCategory } from "./types";

/**
 * Device-level tracking-consent store, persisted to localStorage. Mirrors the
 * module-level store + `useSyncExternalStore` pattern in `ui/Toast.tsx` so any
 * code (including the tracker controller, which runs from an effect) can read
 * and subscribe to consent.
 *
 * Consent is stored versioned: bumping `CONSENT_VERSION` invalidates every
 * stored decision and re-prompts (intended for material policy changes). All
 * localStorage access is wrapped — a parse error, shape mismatch, version
 * mismatch, or private-mode quota error degrades gracefully rather than
 * throwing into the render tree.
 */

export const CONSENT_STORAGE_KEY = "guestbook.consent.v1";
export const CONSENT_VERSION = 1;

export interface ConsentState {
  version: number;
  /** ISO timestamp of when the decision was recorded. */
  timestamp: string;
  /** Per-category grants. `essential` is always `true`. */
  categories: CategoryConsent;
}

/** `null` means no decision recorded yet — the banner should show. */
export type ConsentSnapshot = ConsentState | null;

let state: ConsentSnapshot = read();
const listeners = new Set<() => void>();
let storageBound = false;

function emit(): void {
  for (const listener of listeners) listener();
}

/** Build a full, normalized state from a category map (essential forced on). */
function buildState(categories: CategoryConsent): ConsentState {
  return {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    categories: { ...categories, essential: true },
  };
}

function allCategories(grant: boolean): CategoryConsent {
  const categories = { essential: true } as CategoryConsent;
  for (const category of NON_ESSENTIAL_CATEGORIES) categories[category] = grant;
  return categories;
}

/** Validate an unknown parsed value into a trusted ConsentState (or null). */
function parse(raw: unknown): ConsentSnapshot {
  if (typeof raw !== "object" || raw === null) return null;
  const candidate = raw as Partial<ConsentState>;
  if (candidate.version !== CONSENT_VERSION) return null;
  const categories = candidate.categories;
  if (typeof categories !== "object" || categories === null) return null;
  // Normalize: trust only known categories, force essential on, default the
  // rest to false so a partial stored object can't grant unknown access.
  const normalized = allCategories(false);
  for (const category of NON_ESSENTIAL_CATEGORIES) {
    normalized[category] = (categories as CategoryConsent)[category] === true;
  }
  return {
    version: CONSENT_VERSION,
    timestamp:
      typeof candidate.timestamp === "string"
        ? candidate.timestamp
        : new Date(0).toISOString(),
    categories: normalized,
  };
}

function read(): ConsentSnapshot {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    return parse(JSON.parse(raw));
  } catch {
    // Corrupt JSON, blocked storage, etc. — treat as "no decision".
    return null;
  }
}

function persist(next: ConsentState): void {
  // Update in-memory state first so the session reflects the choice even if
  // the write fails (e.g. private-mode QuotaExceededError).
  state = next;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Non-durable, but the in-memory snapshot keeps the UI consistent.
  }
  emit();
}

/**
 * Stable public API. Safe to reference in effect dependency arrays (the object
 * identity never changes), exactly like Toast's `toastApi`.
 */
export const consent = {
  get(): ConsentSnapshot {
    return state;
  },
  hasDecision(): boolean {
    return state !== null;
  },
  isGranted(category: ConsentCategory): boolean {
    if (category === "essential") return true;
    return state?.categories[category] === true;
  },
  /** Grant every category. */
  acceptAll(): void {
    persist(buildState(allCategories(true)));
  },
  /** Grant only essential; deny all opt-in categories. */
  essentialOnly(): void {
    persist(buildState(allCategories(false)));
  },
  /**
   * Record an explicit per-category choice. Unspecified categories default to
   * denied; essential is always forced on.
   */
  setCategories(partial: Partial<Record<ConsentCategory, boolean>>): void {
    const categories = allCategories(false);
    for (const category of NON_ESSENTIAL_CATEGORIES) {
      categories[category] = partial[category] === true;
    }
    persist(buildState(categories));
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    // Bind the cross-tab sync listener once. The `storage` event fires in
    // *other* tabs when this key changes; the writing tab updates via persist's
    // direct emit. Re-read into state and notify subscribers.
    if (!storageBound && typeof window !== "undefined") {
      storageBound = true;
      window.addEventListener("storage", (event) => {
        if (event.key !== CONSENT_STORAGE_KEY) return;
        state = read();
        emit();
      });
    }
    return () => listeners.delete(listener);
  },
};
